/**
 * 날짜/기간 편집 컨트롤 — 작성폼 트리 위젯(widget:'date'|'date-range')과 행 그리드 필드
 * (DataSourceFieldWidget 동명)가 **같은 컴포넌트**를 쓴다(surface별 재구현 금지 —
 * AnnotatedImageView·SignatureSlotView와 같은 규약).
 *
 * 값 채널은 순수 모듈(dateValue.ts)이 소유한다: date=`yyyy-MM-dd` 문자열,
 * date-range=`{ start?, end? }` 객체.
 *
 * 컨트롤의 **모양**은 호출부가 `className`/`controlStyle`로 주입한다 — 트리 렌더러는 시안
 * 컨트롤 결(inputClass + CONTROL_VISUAL_STYLE), 행 그리드는 셀 입력 결(cellInputClass)로
 * 이미 서로 다르고, 여기서 한쪽을 굳히면 다른 쪽 칸만 결이 튄다.
 */
import { useState } from 'react';
import type { CSSProperties, FocusEvent as ReactFocusEvent } from 'react';
import type { DateRangeValue } from '@safetyhub/shared';
import {
  DATE_PLACEHOLDER_DEFAULT,
  DATE_RANGE_END_PLACEHOLDER_DEFAULT,
  DATE_RANGE_SEPARATOR,
  DATE_RANGE_START_PLACEHOLDER_DEFAULT,
  dateDisplayLabel,
  dateInputValue,
  mergeDateRangeEnd,
  normalizeDateRange,
} from './dateValue';

export interface DateFieldInputProps {
  id?: string;
  /** 라벨 미표시(labelPlacement:'none') 시 실제 컨트롤(투명 date input)에 붙일 접근명. */
  ariaLabel?: string;
  value: unknown;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** 컨트롤 결 클래스 — 호출부(트리=inputClass / 행 그리드=cellInputClass)가 주입. */
  className?: string;
  controlStyle?: CSSProperties;
  onChange: (next: string) => void;
  /**
   * 포커스 이탈 알림(선택) — 표 셀 경로가 "추가 행 입력 확정"·"선택 전용 칸 취소" 판정에 쓴다
   * (renderEditableCell의 다른 입력 위젯과 같은 축). 내부 포커스 상태 갱신 뒤에 호출한다.
   */
  onBlur?: (e: ReactFocusEvent<HTMLElement>) => void;
}

/**
 * date 위젯 편집 컨트롤 — 네이티브 input[type=date]는 표시 문구(placeholder·한글 날짜 표기)를 바꿀 수
 * 없어, 표시 레이어(미선택=안내 문구 / 선택="2026년 7월 21일") 위에 투명 네이티브 input을 겹친다.
 * 클릭 시 showPicker()로 전체 영역에서 날짜 선택 UI 오픈(미지원·비허용이면 네이티브 기본 동작 폴백),
 * 키보드 입력·required 검증은 투명 input이 그대로 받는다. 값 채널은 기존과 동일(yyyy-MM-dd).
 * 포커스 링은 투명 input 위라 안 보이므로 focus 상태를 래퍼 인라인 boxShadow로 재현.
 */
export function DateFieldInput({
  id,
  ariaLabel,
  value,
  placeholder,
  required,
  disabled,
  className,
  controlStyle,
  onChange,
  onBlur,
}: DateFieldInputProps) {
  const [focused, setFocused] = useState(false);
  const label = dateDisplayLabel(value);
  return (
    <div
      className={className}
      style={{
        ...controlStyle,
        // 표시 래퍼는 div라 컨트롤 높이(시안 42px) 안에서 값을 수직 중앙 정렬해야 한다
        // (시안 .field-date/.field-select도 flex+center). 셀 경로(높이 미지정)에서도 무해.
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        cursor: disabled ? undefined : 'pointer',
        ...(focused ? { boxShadow: '0 0 0 2px #6366f1' } : undefined), // brand-500
      }}
    >
      {label === '' ? (
        <span className="text-gray-400">{placeholder || DATE_PLACEHOLDER_DEFAULT}</span>
      ) : (
        <span>{label}</span>
      )}
      <input
        id={id}
        type="date"
        aria-label={ariaLabel}
        value={dateInputValue(value)}
        required={required}
        disabled={disabled}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: disabled ? 'default' : 'pointer',
        }}
        onClick={(e) => {
          // showPicker는 lib.dom 버전에 따라 타입 부재 가능 — 구조 타입으로 옵셔널 호출.
          const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
          try {
            el.showPicker?.();
          } catch {
            // 사용자 제스처 외 호출 등 NotAllowedError — 네이티브 기본 동작(달력 아이콘) 폴백.
          }
        }}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export interface DateRangeFieldInputProps {
  id?: string;
  /** 라벨 미표시 시 접근명 — 두 칸에 "(시작)/(종료)"를 덧붙여 어느 끝인지 밝힌다. */
  ariaLabel?: string;
  value: unknown;
  /** 시작 칸 안내 문구(미지정=기본 '시작일'). */
  startPlaceholder?: string;
  /** 종료 칸 안내 문구(미지정=기본 '종료일'). */
  endPlaceholder?: string;
  /**
   * required는 **시작 칸에만** 건다 — 한쪽만 채워진 기간이 정상이라(shared DateRangeValue doc)
   * 두 칸 모두 필수로 걸면 "언제부터"만 정해진 기간을 아예 저장할 수 없게 된다.
   */
  required?: boolean;
  disabled?: boolean;
  className?: string;
  controlStyle?: CSSProperties;
  onChange: (next: DateRangeValue) => void;
  /**
   * 포커스 이탈 알림(선택) — DateFieldInputProps.onBlur와 같은 축. **두 칸 각각**에서 올라오므로
   * 시작→종료로 넘어가는 이동도 한 번 알린다(표 셀 판정은 relatedTarget으로 같은 행 이동을
   * 걸러내므로 무해하다 — endCellEdit 규약).
   */
  onBlur?: (e: ReactFocusEvent<HTMLElement>) => void;
}

/**
 * date-range 위젯 편집 컨트롤 — DateFieldInput 둘을 '~'로 나란히 둔다.
 *
 * 값은 객체 하나(`{ start?, end? }`)이므로 한쪽을 바꿔도 **다른 쪽을 보존해 병합**한다
 * (교체하면 반대쪽이 조용히 지워진다). 빈 문자열로 지운 끝은 키를 지워 `{}`에 수렴시킨다 —
 * 빈 문자열이 남으면 "값이 있다"고 보는 소비처(요약·되돌리기 비교)와 어긋난다.
 *
 * 한쪽만 입력된 상태를 막지 않는다(검증 없음) — 그것이 정상 상태라는 것이 값 계약이다.
 */
export function DateRangeFieldInput({
  id,
  ariaLabel,
  value,
  startPlaceholder,
  endPlaceholder,
  required,
  disabled,
  className,
  controlStyle,
  onChange,
  onBlur,
}: DateRangeFieldInputProps) {
  const range = normalizeDateRange(value);
  // 병합 규칙(반대쪽 보존 + 빈 문자열은 키 삭제)은 저장 계약이라 순수 함수가 소유한다.
  const withEnd = (key: 'start' | 'end', next: string): DateRangeValue =>
    mergeDateRangeEnd(value, key, next);
  return (
    // 두 칸 + 구분자를 한 줄에 — gap·구분자 색은 임계 레이아웃이라 인라인이 소유(rc Tailwind 규약).
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      <div style={{ flex: '1 1 0%', minWidth: 0 }}>
        <DateFieldInput
          id={id}
          ariaLabel={ariaLabel === undefined ? undefined : `${ariaLabel} (시작)`}
          value={range.start}
          placeholder={startPlaceholder || DATE_RANGE_START_PLACEHOLDER_DEFAULT}
          required={required}
          disabled={disabled}
          className={className}
          controlStyle={controlStyle}
          onChange={(next) => onChange(withEnd('start', next))}
          onBlur={onBlur}
        />
      </div>
      <span aria-hidden="true" style={{ color: '#94a3b8', flexShrink: 0 }}>
        {DATE_RANGE_SEPARATOR}
      </span>
      <div style={{ flex: '1 1 0%', minWidth: 0 }}>
        <DateFieldInput
          ariaLabel={ariaLabel === undefined ? undefined : `${ariaLabel} (종료)`}
          value={range.end}
          placeholder={endPlaceholder || DATE_RANGE_END_PLACEHOLDER_DEFAULT}
          disabled={disabled}
          className={className}
          controlStyle={controlStyle}
          onChange={(next) => onChange(withEnd('end', next))}
          onBlur={onBlur}
        />
      </div>
    </div>
  );
}
